import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import SEOHead from '../../components/SEOHead';
import Navigation from '../../components/Navigation';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function Home() {
  const [heroSection, setHeroSection] = useState<any>(null);
  const [platforms, setPlatforms] = useState<any[]>([]);
  const [features, setFeatures] = useState<any[]>([]);
  const [testimonials, setTestimonials] = useState<any[]>([]);
  const [companyInfo, setCompanyInfo] = useState<any>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      // Fetch hero section
      const { data: heroData } = await supabase
        .from('hero_sections')
        .select('*')
        .eq('page', 'home')
        .eq('is_active', true)
        .order('display_order', { ascending: true })
        .limit(1)
        .single();

      if (heroData) setHeroSection(heroData);

      // Fetch platforms
      const { data: platformsData } = await supabase
        .from('platforms')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (platformsData) setPlatforms(platformsData);

      // Fetch features
      const { data: featuresData } = await supabase
        .from('features')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true })
        .limit(3);

      if (featuresData) setFeatures(featuresData);

      // Fetch testimonials
      const { data: testimonialsData } = await supabase
        .from('testimonials')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (testimonialsData) setTestimonials(testimonialsData);

      // Fetch company info
      const { data: companyData } = await supabase
        .from('company_info')
        .select('*');

      if (companyData) {
        const infoObj: any = {};
        companyData.forEach((item: any) => {
          infoObj[item.key] = item.value;
        });
        setCompanyInfo(infoObj);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching content:', error);
      setLoading(false);
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Solution Platform",
    "description": "Enterprise software development platform suite with low-code development, data flow management, and business intelligence",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "99",
      "priceCurrency": "USD"
    },
    "provider": {
      "@type": "Organization",
      "name": "Solution Platform",
      "url": import.meta.env.VITE_SITE_URL
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin"></i>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Solution Platform - Enterprise Low-Code, Data Flow & BI Solutions"
        description="Transform your enterprise with our integrated platform suite featuring Codee low-code development, Fusion data flow management, and advanced BI analytics. Partner with us to accelerate software development and unlock new revenue opportunities."
        keywords="low-code platform, data flow management, business intelligence, enterprise software, partner program"
        canonical="/"
        structuredData={structuredData}
      />
      
      {/* Navigation */}
      <Navigation />

      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('${heroSection?.background_image || 'https://public.readdy.ai/ai/img_res/8f8e3c447d38ebfeaa0ab5fab7822c03.jpg'}')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-left max-w-3xl ml-0 lg:ml-16">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight">
              {heroSection?.title || 'Transform Your Enterprise with Our Complete Solution Platform'}
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-200 mb-6 sm:mb-8 leading-relaxed">
              {heroSection?.subtitle || 'Empower your business with Codee\'s low-code development, Fusion\'s data flow management, and advanced BI analytics - all in one integrated ecosystem.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link to={heroSection?.cta_link || '/partners'} className="bg-blue-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-blue-700 transition-colors text-center whitespace-nowrap cursor-pointer">
                {heroSection?.cta_text || 'Become a Partner'}
              </Link>
              <Link to="/contact" className="border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer text-center">
                Schedule Demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Overview */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-4">
              Three Powerful Platforms, One Complete Solution
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Our integrated suite of platforms provides everything you need for modern enterprise software development and data management.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {platforms.length > 0 ? platforms.map((platform) => (
              <div key={platform.id} className="bg-white rounded-xl shadow-lg p-6 sm:p-8 hover:shadow-xl transition-shadow">
                <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-lg flex items-center justify-center mb-4 sm:mb-6`} style={{ backgroundColor: `${platform.color}20` }}>
                  <i className={`${platform.icon || 'ri-code-s-slash-line'} text-xl sm:text-2xl`} style={{ color: platform.color }}></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">{platform.name}</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                  {platform.description}
                </p>
                {platform.features && Array.isArray(platform.features) && (
                  <ul className="space-y-2 sm:space-y-3 text-sm sm:text-base text-gray-600">
                    {platform.features.slice(0, 3).map((feature: string, idx: number) => (
                      <li key={idx} className="flex items-center">
                        <i className="ri-check-line text-green-500 mr-2 sm:mr-3 flex-shrink-0"></i>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )) : (
              <>
                <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 hover:shadow-xl transition-shadow">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-4 sm:mb-6">
                    <i className="ri-code-s-slash-line text-xl sm:text-2xl text-blue-600"></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Codee</h3>
                  <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                    Low Code and No Code platform that accelerates application development with visual programming and drag-and-drop interfaces.
                  </p>
                  <ul className="space-y-2 sm:space-y-3 text-sm sm:text-base text-gray-600">
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-500 mr-2 sm:mr-3 flex-shrink-0"></i>
                      <span>Visual Development Environment</span>
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-500 mr-2 sm:mr-3 flex-shrink-0"></i>
                      <span>Pre-built Components Library</span>
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-500 mr-2 sm:mr-3 flex-shrink-0"></i>
                      <span>Rapid Prototyping Tools</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 hover:shadow-xl transition-shadow">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-purple-100 rounded-lg flex items-center justify-center mb-4 sm:mb-6">
                    <i className="ri-flow-chart text-xl sm:text-2xl text-purple-600"></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Fusion</h3>
                  <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                    Data Flow platform that seamlessly integrates, transforms, and manages data across your entire enterprise ecosystem.
                  </p>
                  <ul className="space-y-2 sm:space-y-3 text-sm sm:text-base text-gray-600">
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-500 mr-2 sm:mr-3 flex-shrink-0"></i>
                      <span>Real-time Data Integration</span>
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-500 mr-2 sm:mr-3 flex-shrink-0"></i>
                      <span>Advanced ETL Processes</span>
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-500 mr-2 sm:mr-3 flex-shrink-0"></i>
                      <span>Data Quality Management</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 hover:shadow-xl transition-shadow">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-green-100 rounded-lg flex items-center justify-center mb-4 sm:mb-6">
                    <i className="ri-bar-chart-box-line text-xl sm:text-2xl text-green-600"></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">BI Platform</h3>
                  <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                    Business Intelligence platform that transforms raw data into actionable insights with powerful analytics and visualization tools.
                  </p>
                  <ul className="space-y-2 sm:space-y-3 text-sm sm:text-base text-gray-600">
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-500 mr-2 sm:mr-3 flex-shrink-0"></i>
                      <span>Interactive Dashboards</span>
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-500 mr-2 sm:mr-3 flex-shrink-0"></i>
                      <span>Advanced Analytics Engine</span>
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-500 mr-2 sm:mr-3 flex-shrink-0"></i>
                      <span>Custom Report Builder</span>
                    </li>
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                Why Choose Our Solution Platform?
              </h2>
              <div className="space-y-4 sm:space-y-6">
                {features.length > 0 ? features.map((feature) => (
                  <div key={feature.id} className="flex items-start">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0`} style={{ backgroundColor: `${feature.category === 'Development' ? '#3B82F6' : feature.category === 'Integration' ? '#A855F7' : '#10B981'}20` }}>
                      <i className={`${feature.icon || 'ri-rocket-line'} text-lg sm:text-xl`} style={{ color: feature.category === 'Development' ? '#3B82F6' : feature.category === 'Integration' ? '#A855F7' : '#10B981' }}></i>
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1 sm:mb-2">{feature.title}</h3>
                      <p className="text-sm sm:text-base text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                )) : (
                  <>
                    <div className="flex items-start">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                        <i className="ri-rocket-line text-lg sm:text-xl text-blue-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1 sm:mb-2">Accelerated Development</h3>
                        <p className="text-sm sm:text-base text-gray-600">Reduce development time by up to 80% with our low-code platform and pre-built components.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                        <i className="ri-links-line text-lg sm:text-xl text-purple-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1 sm:mb-2">Seamless Integration</h3>
                        <p className="text-sm sm:text-base text-gray-600">Connect all your systems and data sources with our unified integration platform.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                        <i className="ri-eye-line text-lg sm:text-xl text-green-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1 sm:mb-2">Data-Driven Insights</h3>
                        <p className="text-sm sm:text-base text-gray-600">Make informed decisions with real-time analytics and comprehensive business intelligence.</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            <div className="relative mt-8 lg:mt-0">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20business%20team%20analyzing%20data%20on%20multiple%20monitors%20showing%20charts%2C%20graphs%2C%20and%20analytics%20dashboards%20in%20a%20modern%20office%20setting%20with%20collaborative%20workspace%2C%20bright%20lighting%2C%20and%20technology-focused%20environment&width=600&height=400&seq=benefits-image&orientation=landscape"
                alt="Business Intelligence Analytics Dashboard"
                className="rounded-xl shadow-lg object-cover w-full h-64 sm:h-80 lg:h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Partner CTA */}
      <section className="py-12 sm:py-16 lg:py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 sm:mb-6 px-4">
            Ready to Partner with Us?
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-blue-100 mb-6 sm:mb-8 max-w-3xl mx-auto px-4">
            Join our partner network and help enterprises transform their software development processes. Unlock new revenue opportunities and provide cutting-edge solutions to your clients.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <Link to="/partners" className="bg-white text-blue-600 px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              Become a Partner
            </Link>
            <Link to="/resources" className="border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
              View Resources
            </Link>
          </div>
        </div>
      </section>

      {/* Company Info Banner */}
      <section className="py-6 sm:py-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-3 sm:mb-4">
              <img 
                src="https://static.readdy.ai/image/feaac7b19917aec559683ef9ad3d4cd6/f01248d0a1f191b5d9d27b9480b2a552.jpeg" 
                alt="Allgo Digital Logo" 
                className="h-10 sm:h-12 w-auto filter brightness-0 invert"
              />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">
              {companyInfo.company_name || 'Allgo Digital Sdn Bhd'}
            </h3>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-2 md:space-y-0 md:space-x-8 text-sm sm:text-base text-gray-300 px-4">
              <div className="flex items-start md:items-center">
                <i className="ri-map-pin-line mr-2 mt-1 md:mt-0 flex-shrink-0"></i>
                <span className="text-left md:text-center">{companyInfo.address || 'E-1-6, Kuchai Business Park Jalan Kuchai Lama, Off, Jalan 1/127, Kuchai Lama, 58200 Kuala Lumpur'}</span>
              </div>
              <div className="flex items-center">
                <i className="ri-mail-line mr-2"></i>
                <span>{companyInfo.email || 'info@allgo-digital.com'}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            <div>
              <img 
                src="https://static.readdy.ai/image/feaac7b19917aec559683ef9ad3d4cd6/f01248d0a1f191b5d9d27b9480b2a552.jpeg" 
                alt="Allgo Digital Logo" 
                className="h-10 sm:h-12 w-auto mb-3 sm:mb-4 filter brightness-0 invert"
              />
              <p className="text-sm sm:text-base text-gray-400">
                Transforming enterprise software development with integrated low-code, data flow, and BI solutions.
              </p>
            </div>
            
            <div>
              <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Platforms</h4>
              <ul className="space-y-2 text-sm sm:text-base text-gray-400">
                <li><Link to="/platforms" className="hover:text-white cursor-pointer">Codee Platform</Link></li>
                <li><Link to="/platforms" className="hover:text-white cursor-pointer">Fusion Platform</Link></li>
                <li><Link to="/platforms" className="hover:text-white cursor-pointer">BI Platform</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Resources</h4>
              <ul className="space-y-2 text-sm sm:text-base text-gray-400">
                <li><Link to="/resources" className="hover:text-white cursor-pointer">Resources</Link></li>
                <li><Link to="/features" className="hover:text-white cursor-pointer">Features</Link></li>
                <li><Link to="/partners" className="hover:text-white cursor-pointer">Partner Program</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Contact</h4>
              <ul className="space-y-2 text-sm sm:text-base text-gray-400">
                <li>{companyInfo.email || 'info@allgo-digital.com'}</li>
                <li>{companyInfo.phone || '+60 3-7931 8888'}</li>
                <li>Enterprise Solutions</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-6 sm:mt-8 pt-6 sm:pt-8 text-center text-sm sm:text-base text-gray-400">
            <p>&copy; 2024 Allgo Digital Sdn Bhd. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
